import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinancialAlertsPanel = ({ alerts = [] }) => {
  const [filter, setFilter] = useState('all'); // all, critical, warning, info
  const [sortBy, setSortBy] = useState('timestamp'); // timestamp, type, amount

  const formatCurrency = (value) => {
    const numValue = typeof value === 'string' ? parseFloat(value?.replace(/[$,+-]/g, '')) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(Math.abs(numValue));
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(timestamp)) / 1000);
    
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getAlertBackground = (type) => {
    switch (type) {
      case 'critical': return 'bg-error/10';
      case 'warning': return 'bg-warning/10';
      case 'info': return 'bg-primary/10';
      default: return 'bg-muted/10';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'budget': return 'Target';
      case 'cashflow': return 'CreditCard';
      case 'revenue': return 'TrendingUp';
      case 'payment': return 'DollarSign';
      case 'approval': return 'CheckCircle';
      default: return 'Bell';
    }
  };

  const getFilteredAlerts = () => {
    let filtered = alerts;
    
    if (filter !== 'all') {
      filtered = filtered?.filter(alert => alert?.type === filter);
    }
    
    switch (sortBy) {
      case 'type':
        return filtered?.sort((a, b) => {
          const order = { critical: 3, warning: 2, info: 1 };
          return (order?.[b?.type] || 0) - (order?.[a?.type] || 0);
        });
      case 'amount':
        return filtered?.sort((a, b) => {
          const aAmount = Math.abs(parseFloat(a?.amount?.replace(/[$,+-]/g, '') || 0));
          const bAmount = Math.abs(parseFloat(b?.amount?.replace(/[$,+-]/g, '') || 0));
          return bAmount - aAmount;
        });
      default:
        return filtered?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  };

  const getAlertStats = () => {
    return {
      critical: alerts?.filter(a => a?.type === 'critical')?.length,
      warning: alerts?.filter(a => a?.type === 'warning')?.length,
      info: alerts?.filter(a => a?.type === 'info')?.length,
      total: alerts?.length
    };
  };

  const filteredAlerts = getFilteredAlerts();
  const stats = getAlertStats();

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Financial Alerts</h3>
            <p className="text-sm text-muted-foreground">
              {stats?.total} alerts • {stats?.critical} critical • {stats?.warning} warnings
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === 'all' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({stats?.total})
            </button>
            <button
              onClick={() => setFilter('critical')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === 'critical' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Critical ({stats?.critical})
            </button>
            <button
              onClick={() => setFilter('warning')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === 'warning' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Warning ({stats?.warning})
            </button>
          </div>
          <Button variant="ghost" size="sm" iconName="Settings">
            Configure
          </Button>
        </div>
      </div>
      {/* Sort Options */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setSortBy('timestamp')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                sortBy === 'timestamp' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Recent
            </button>
            <button
              onClick={() => setSortBy('type')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                sortBy === 'type' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Priority
            </button>
            <button
              onClick={() => setSortBy('amount')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                sortBy === 'amount' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Amount
            </button>
          </div>
        </div>
        <Button variant="ghost" size="sm" iconName="Trash2">
          Clear All
        </Button>
      </div>
      {/* Alerts List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredAlerts?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
            <h4 className="text-lg font-medium text-foreground mb-1">All Clear!</h4>
            <p className="text-muted-foreground">No financial alerts at this time</p>
          </div>
        ) : (
          filteredAlerts?.map((alert) => (
            <div 
              key={alert?.id} 
              className={`border rounded-lg p-4 transition-all hover:shadow-md ${getAlertBackground(alert?.type)} border-l-4`}
              style={{ borderLeftColor: alert?.type === 'critical' ? '#EF4444' : alert?.type === 'warning' ? '#F59E0B' : '#3B82F6' }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getAlertBackground(alert?.type)}`}>
                    <Icon 
                      name={getAlertIcon(alert?.type)} 
                      size={16} 
                      className={getAlertColor(alert?.type)}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-foreground">{alert?.title}</h4>
                      <div className="flex items-center space-x-1">
                        <Icon 
                          name={getCategoryIcon(alert?.category)} 
                          size={12} 
                          className="text-muted-foreground"
                        />
                        <span className="text-xs text-muted-foreground capitalize">{alert?.category}</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{alert?.message}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(alert?.timestamp)}
                        </span>
                        {alert?.amount && (
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            alert?.amount?.startsWith('+') ? 'bg-success/20 text-success' : 
                            alert?.amount?.startsWith('-') ? 'bg-error/20 text-error' : 
                            'bg-primary/20 text-primary'
                          }`}>
                            {alert?.amount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Button variant="ghost" size="sm">
                    <Icon name="Eye" size={14} />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Icon name="X" size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Action Buttons */}
      <div className="flex items-center space-x-2 mt-6 pt-4 border-t border-border">
        <Button variant="outline" size="sm" iconName="Bell" className="flex-1">
          Manage Alerts
        </Button>
        <Button variant="outline" size="sm" iconName="Mail" className="flex-1">
          Email Digest
        </Button>
        <Button variant="default" size="sm" iconName="Zap" className="flex-1">
          Auto Actions
        </Button>
      </div>
    </div>
  );
};

export default FinancialAlertsPanel;